import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import AdminShell from '../components/admin/AdminShell';
import { buildApiUrl } from '../utils/api/config';
import './ManageVillages.css';

function ManageVillages() {
  const navigate = useNavigate();
  const [toast, setToast] = useState(null);
  const [loading, setLoading] = useState(false);
  
  const [districts, setDistricts] = useState([]);
  const [blocks, setBlocks] = useState([]);
  const [villages, setVillages] = useState([]);
  const [selectedDistrict, setSelectedDistrict] = useState('');
  const [selectedBlock, setSelectedBlock] = useState('');
  const [selectedVillage, setSelectedVillage] = useState('');
  const [tab, setTab] = useState('village'); // 'village' or 'news'
  const [imagePreview, setImagePreview] = useState(null);
  const [editingVillageId, setEditingVillageId] = useState('');

  const [villageForm, setVillageForm] = useState({
    name: '',
    districtId: '',
    districtName: '',
    blockId: '',
    blockName: '',
    description: '',
    population: 0
  });

  const [newsForm, setNewsForm] = useState({
    title: '',
    shortDescription: '',
    description: '',
    image: null,
    videoUrl: '',
    author: '',
    date: new Date().toISOString().split('T')[0],
    category: 'chitrakoot',
    subCategory: '',
    tags: ''
  });

  const fetchDistricts = useCallback(async () => {
    try {
      const response = await fetch(buildApiUrl('/locations/districts'));
      const data = await response.json();
      if (data.success) {
        setDistricts(data.data);
      }
    } catch (error) {
      console.error('Error fetching districts:', error);
      showToast('❌ Failed to load districts', 'error');
    }
  }, []);

  const fetchBlocksByDistrict = useCallback(async (districtId) => {
    try {
      const response = await fetch(buildApiUrl(`/locations/blocks/district/${districtId}`));
      const data = await response.json();
      if (data.success) {
        setBlocks(data.data);
      }
    } catch (error) {
      console.error('Error fetching blocks:', error);
      showToast('❌ Failed to load blocks', 'error');
    }
  }, []);

  const fetchVillagesByBlock = useCallback(async (blockId) => {
    try {
      const response = await fetch(buildApiUrl(`/locations/villages/block/${blockId}`));
      const data = await response.json();
      if (data.success) {
        setVillages(data.data);
      }
    } catch (error) {
      console.error('Error fetching villages:', error);
      showToast('❌ Failed to load villages', 'error');
    }
  }, []);

  useEffect(() => {
    fetchDistricts();
  }, [fetchDistricts]);

  useEffect(() => {
    if (selectedDistrict) {
      fetchBlocksByDistrict(selectedDistrict);
    } else {
      setBlocks([]);
      setSelectedBlock('');
      setVillages([]);
      setSelectedVillage('');
    }
  }, [fetchBlocksByDistrict, selectedDistrict]);

  useEffect(() => {
    if (selectedBlock) {
      fetchVillagesByBlock(selectedBlock);
    } else {
      setVillages([]);
      setSelectedVillage('');
    }
  }, [fetchVillagesByBlock, selectedBlock]);

  const showToast = (message, type = 'success') => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 3000);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setVillageForm(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleDistrictSelect = (e) => {
    const districtId = e.target.value;
    setSelectedDistrict(districtId);
    setSelectedBlock('');
    const selectedDist = districts.find(d => d._id === districtId);
    if (selectedDist) {
      setVillageForm(prev => ({
        ...prev,
        districtId,
        districtName: selectedDist.name,
        blockId: '',
        blockName: ''
      }));
    }
  };

  const handleBlockSelect = (e) => {
    const blockId = e.target.value;
    setSelectedBlock(blockId);
    const selectedBlk = blocks.find(b => b._id === blockId);
    if (selectedBlk) {
      setVillageForm(prev => ({
        ...prev,
        blockId,
        blockName: selectedBlk.name
      }));
    }
  };

  const handleAddVillage = async (e) => {
    e.preventDefault();

    if (!villageForm.name || !villageForm.districtId || !villageForm.blockId) {
      showToast('❌ गांव का नाम, जिला और ब्लॉक आवश्यक हैं', 'error');
      return;
    }

    try {
      setLoading(true);
      const token = localStorage.getItem('authToken');

      const response = await fetch(buildApiUrl(editingVillageId ? `/locations/villages/${editingVillageId}` : '/locations/villages/add'), {
        method: editingVillageId ? 'PUT' : 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          name: villageForm.name,
          district: villageForm.districtId,
          districtName: villageForm.districtName,
          block: villageForm.blockId,
          blockName: villageForm.blockName,
          description: villageForm.description,
          population: parseInt(villageForm.population) || 0
        })
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || 'Failed to add village');
      }

      showToast(editingVillageId ? '✅ गांव सफलतापूर्वक अपडेट किया गया!' : '✅ गांव सफलतापूर्वक जोड़ा गया!');
      setEditingVillageId('');
      setVillageForm(prev => ({
        ...prev,
        name: '',
        description: '',
        population: 0
      }));
      fetchVillagesByBlock(villageForm.blockId);
    } catch (error) {
      showToast(`❌ ${error.message}`, 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    navigate('/admin/dashboard');
  };

  const handleEditVillage = async (village) => {
    setTab('village');
    setEditingVillageId(village._id);
    setSelectedDistrict(village.district);
    await fetchBlocksByDistrict(village.district);
    setSelectedBlock(village.block);
    await fetchVillagesByBlock(village.block);
    setVillageForm({
      name: village.name || '',
      districtId: village.district,
      districtName: village.districtName || '',
      blockId: village.block,
      blockName: village.blockName || '',
      description: village.description || '',
      population: village.population || 0
    });
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleDeleteVillage = async (id) => {
    if (!window.confirm('क्या आप यह गांव हटाना चाहते हैं?')) {
      return;
    }

    try {
      const token = localStorage.getItem('authToken');
      const response = await fetch(buildApiUrl(`/locations/villages/${id}`), {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      const data = await response.json();
      if (!response.ok) {
        throw new Error(data.message || 'Failed to delete village');
      }
      showToast('✅ गांव सफलतापूर्वक हटाया गया!');
      if (selectedBlock) {
        fetchVillagesByBlock(selectedBlock);
      }
    } catch (error) {
      showToast(`❌ ${error.message}`, 'error');
    }
  };

  const handleVillageSelect = (e) => {
    const villageId = e.target.value;
    setSelectedVillage(villageId);
  };

  const handleNewsInputChange = (e) => {
    const { name, value } = e.target;
    setNewsForm(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setNewsForm(prev => ({
        ...prev,
        image: file
      }));
      const preview = URL.createObjectURL(file);
      setImagePreview(preview);
    }
  };

  const handleAddNews = async (e) => {
    e.preventDefault();

    if (!newsForm.title || !newsForm.shortDescription || !newsForm.description || !selectedVillage) {
      showToast('❌ समाचार का शीर्षक, संक्षिप्त विवरण, विवरण और गांव आवश्यक हैं', 'error');
      return;
    }

    if (!newsForm.image) {
      showToast('❌ कृपया एक छवि चुनें', 'error');
      return;
    }

    try {
      setLoading(true);
      const token = localStorage.getItem('authToken');
      const selectedVillageData = villages.find(v => v._id === selectedVillage);

      const formData = new FormData();
      formData.append('title', newsForm.title);
      formData.append('shortDescription', newsForm.shortDescription);
      formData.append('description', newsForm.description);
      formData.append('category', newsForm.category);
      formData.append('subCategory', newsForm.subCategory || '');
      formData.append('tags', newsForm.tags || '');
      formData.append('isPublished', 'true');
      formData.append('villageId', selectedVillage);
      formData.append('blockId', selectedVillageData?.block);
      
      // Add video URL if provided
      if (newsForm.videoUrl) {
        formData.append('videoUrl', newsForm.videoUrl);
      }
      
      if (newsForm.image) {
        formData.append('featuredImage', newsForm.image);
      }

      // Debug: Log FormData contents
      console.log('📤 ManageVillages - Sending News:');
      console.log('   Form State videoUrl:', newsForm.videoUrl);
      console.log('   FormData entries:');
      for (let [key, value] of formData.entries()) {
        if (key !== 'featuredImage') {
          console.log(`      ${key}: ${value}`);
        } else {
          console.log(`      ${key}: (File object)`);
        }
      }

      const response = await fetch(buildApiUrl('/news/add'), {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        },
        body: formData
      });

      const data = await response.json();
      console.log('✅ Backend Response:', data);

      if (!response.ok) {
        throw new Error(data.message || 'Failed to add news');
      }

      showToast('✅ समाचार सफलतापूर्वक जोड़ा गया!');
      setNewsForm({
        title: '',
        shortDescription: '',
        description: '',
        image: null,
        videoUrl: '',
        author: '',
        date: new Date().toISOString().split('T')[0],
        category: 'chitrakoot',
        subCategory: '',
        tags: ''
      });
      setImagePreview(null);
    } catch (error) {
      showToast(`❌ ${error.message}`, 'error');
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      {toast && (
        <div className={`toast-notification ${toast.type}`}>
          {toast.message}
        </div>
      )}

      <AdminShell
        eyebrow="Village Coverage"
        title="Manage villages and local news"
        description="Add villages to mapped blocks, then publish village-level news with media and category details from the same workspace."
        primaryAction={{
          label: tab === 'village' ? 'Switch to news' : 'Switch to villages',
          onClick: () => setTab((current) => (current === 'village' ? 'news' : 'village'))
        }}
        secondaryAction={{ label: 'Dashboard', onClick: handleCancel }}
        meta={[
          { label: 'Districts', value: String(districts.length) },
          { label: 'Villages shown', value: String(villages.length) }
        ]}
      >
        <div className="manage-villages-container">
          <div className="villages-wrapper">

        {/* Tab Navigation */}
        <div className="tab-navigation">
          <button
            className={`tab-btn ${tab === 'village' ? 'active' : ''}`}
            onClick={() => setTab('village')}
          >
            🏘️ गांव जोड़ें
          </button>
          <button
            className={`tab-btn ${tab === 'news' ? 'active' : ''}`}
            onClick={() => setTab('news')}
          >
            📰 समाचार जोड़ें
          </button>
        </div>

        {/* Village Tab */}
        {tab === 'village' && (
        <div className="form-container">
          <h2>{editingVillageId ? 'गांव संपादित करें' : 'नया गांव जोड़ें'}</h2>
          <form onSubmit={handleAddVillage} className="form">
            <div className="form-row">
              <div className="form-group">
                <label>जिला चुनें *</label>
                <select
                  value={selectedDistrict}
                  onChange={handleDistrictSelect}
                  required
                  className="form-select"
                >
                  <option value="">-- जिला चुनें --</option>
                  {districts.map(district => (
                    <option key={district._id} value={district._id}>
                      {district.name}
                    </option>
                  ))}
                </select>
              </div>

              <div className="form-group">
                <label>ब्लॉक चुनें *</label>
                <select
                  value={selectedBlock}
                  onChange={handleBlockSelect}
                  required
                  className="form-select"
                  disabled={!selectedDistrict}
                >
                  <option value="">-- ब्लॉक चुनें --</option>
                  {blocks.map(block => (
                    <option key={block._id} value={block._id}>
                      {block.name}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div className="form-group">
              <label>गांव का नाम *</label>
              <input
                type="text"
                name="name"
                value={villageForm.name}
                onChange={handleInputChange}
                placeholder="गांव का नाम दर्ज करें..."
                required
              />
            </div>

            <div className="form-row">
              <div className="form-group">
                <label>जनसंख्या</label>
                <input
                  type="number"
                  name="population"
                  value={villageForm.population}
                  onChange={handleInputChange}
                  placeholder="जनसंख्या दर्ज करें..."
                  min="0"
                />
              </div>
            </div>

            <div className="form-group">
              <label>विवरण</label>
              <textarea
                name="description"
                value={villageForm.description}
                onChange={handleInputChange}
                placeholder="गांव का विवरण..."
                rows={4}
              />
            </div>

            <div className="form-actions">
              <button type="submit" disabled={loading || !selectedBlock} className="btn-submit">
                {loading ? 'सेव हो रहा है...' : editingVillageId ? '✓ गांव अपडेट करें' : '✓ गांव जोड़ें'}
              </button>
              <button type="button" onClick={editingVillageId ? () => {
                setEditingVillageId('');
                setVillageForm(prev => ({ ...prev, name: '', description: '', population: 0 }));
              } : handleCancel} className="btn-cancel">
                {editingVillageId ? 'Reset' : '✕ रद्द करें'}
              </button>
            </div>
          </form>

          {/* Villages List */}
          {selectedBlock && (
            <div className="list-container">
              <h3>
                {blocks.find(b => b._id === selectedBlock)?.name} ब्लॉक के गांव
              </h3>
              <div className="list">
                {villages.length > 0 ? (
                  villages.map(village => (
                    <div key={village._id} className="list-item">
                      <div className="item-info">
                        <h4>🏘️ {village.name}</h4>
                        <p className="item-meta">
                          <span>📍 {village.blockName}</span>
                          {village.population > 0 && (
                            <span>👥 जनसंख्या: {village.population.toLocaleString('hi-IN')}</span>
                          )}
                        </p>
                        {village.description && (
                          <p className="item-description">{village.description}</p>
                        )}
                      </div>
                      <div className="list-item-actions">
                        <button type="button" className="btn-cancel action-btn" onClick={() => handleEditVillage(village)}>
                          Edit
                        </button>
                        <button type="button" className="btn-submit danger action-btn" onClick={() => handleDeleteVillage(village._id)}>
                          Delete
                        </button>
                      </div>
                    </div>
                  ))
                ) : (
                  <p className="no-data">इस ब्लॉक में कोई गांव नहीं है</p>
                )}
              </div>
            </div>
          )}
        </div>
        )}

        {/* News Tab */}
        {tab === 'news' && (
        <div className="form-container">
          <h2>नया समाचार जोड़ें</h2>
          <form onSubmit={handleAddNews} className="form">
            {/* Village Selection */}
            <div className="form-group">
              <label>गांव चुनें *</label>
              <select
                value={selectedVillage}
                onChange={handleVillageSelect}
                required
                className="form-select"
              >
                <option value="">-- गांव चुनें --</option>
                {villages.map(village => (
                  <option key={village._id} value={village._id}>
                    {village.name} ({village.blockName})
                  </option>
                ))}
              </select>
            </div>

            {/* Title */}
            <div className="form-group">
              <label>समाचार का शीर्षक *</label>
              <input
                type="text"
                name="title"
                value={newsForm.title}
                onChange={handleNewsInputChange}
                placeholder="समाचार का शीर्षक..."
                required
              />
            </div>

            {/* Short Description */}
            <div className="form-group">
              <label>संक्षिप्त विवरण</label>
              <textarea
                name="shortDescription"
                value={newsForm.shortDescription}
                onChange={handleNewsInputChange}
                placeholder="संक्षिप्त विवरण..."
                rows={2}
              />
            </div>

            {/* Description */}
            <div className="form-group">
              <label>विस्तृत विवरण</label>
              <textarea
                name="description"
                value={newsForm.description}
                onChange={handleNewsInputChange}
                placeholder="समाचार का विस्तृत विवरण..."
                rows={4}
              />
            </div>

            {/* Image and Video */}
            <div className="form-row">
              <div className="form-group">
                <label>छवि</label>
                <input
                  type="file"
                  accept="image/*"
                  onChange={handleImageChange}
                  className="file-input"
                />
                {imagePreview && (
                  <div className="image-preview">
                    <img src={imagePreview} alt="Preview" />
                  </div>
                )}
              </div>

              <div className="form-group">
                <label>वीडियो यूआरएल</label>
                <input
                  type="url"
                  name="videoUrl"
                  value={newsForm.videoUrl}
                  onChange={handleNewsInputChange}
                  placeholder="Youtube या Vimeo URL..."
                />
              </div>
            </div>

            {/* Author and Date */}
            <div className="form-row">
              <div className="form-group">
                <label>लेखक का नाम</label>
                <input
                  type="text"
                  name="author"
                  value={newsForm.author}
                  onChange={handleNewsInputChange}
                  placeholder="लेखक का नाम..."
                />
              </div>

              <div className="form-group">
                <label>तारीख</label>
                <input
                  type="date"
                  name="date"
                  value={newsForm.date}
                  onChange={handleNewsInputChange}
                />
              </div>
            </div>

            {/* Category and Tags */}
            <div className="form-row">
              <div className="form-group">
                <label>समाचार श्रेणी</label>
                <select
                  name="subCategory"
                  value={newsForm.subCategory}
                  onChange={handleNewsInputChange}
                >
                  <option value="">-- श्रेणी चुनें --</option>
                  <option value="स्थानीय">स्थानीय</option>
                  <option value="सामाजिक">सामाजिक</option>
                  <option value="राजनीति">राजनीति</option>
                  <option value="शिक्षा">शिक्षा</option>
                  <option value="स्वास्थ्य">स्वास्थ्य</option>
                  <option value="खेल">खेल</option>
                  <option value="मनोरंजन">मनोरंजन</option>
                  <option value="अन्य">अन्य</option>
                </select>
              </div>

              <div className="form-group">
                <label>टैग्स (अल्पविराम से अलग करें)</label>
                <input
                  type="text"
                  name="tags"
                  value={newsForm.tags}
                  onChange={handleNewsInputChange}
                  placeholder="टैग1, टैग2, टैग3..."
                />
              </div>
            </div>

            <div className="form-actions">
              <button type="submit" disabled={loading || !selectedVillage} className="btn-submit">
                {loading ? 'जोड़ा जा रहा है...' : '✓ समाचार जोड़ें'}
              </button>
              <button type="button" onClick={handleCancel} className="btn-cancel">
                ✕ रद्द करें
              </button>
            </div>
          </form>
        </div>
        )}
          </div>
        </div>
      </AdminShell>
    </>
  );
}

export default ManageVillages;
